// Rapport imprimable d'un projet (PDF) — synthèse complète sur une fiche.
// Utilise jsPDF + jspdf-autotable (déjà présents dans le projet).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../../../utils/axiosInstance";   // ⚠️ même import que dans ProjetDetail.js
import {
    API, unwrap,
    findType, findStatut, findPriorite, findEtape,
    findStatutTache, findTypeJournal, findGravite, findCategorieDepense,
    DOC_COMMERCIAL_LABELS,
} from "./projetsCommon";

const BLEU = [42, 120, 214];      // #2a78d6
const ORANGE = [235, 104, 52];    // #eb6834
const GRIS = [110, 120, 130];
const LOGO = `${process.env.PUBLIC_URL}/dist/img/logo_ecogen.png`;   // même logo que les documents

// Montants : jsPDF ne sait pas afficher l'espace insécable fine de toLocaleString
const dh = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const s = Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return s.replace(/[  ]/g, " ") + " DH";
};
const dt = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "—");
const pct = (v) => (v === null || v === undefined ? "—" : `${v} %`);
const h = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("fr-FR")} h`);

const charger = async (url) => {
    try { return unwrap(await axiosInstance.get(url)); } catch { return null; }
};

export async function exporterRapportProjet(projetId, { imprimer = false } = {}) {
    // 1. Données (chaque bloc est facultatif : une erreur n'empêche pas le rapport)
    const [projet, finances, planning, journal, membres] = await Promise.all([
        charger(API.projet(projetId)),
        charger(API.finances(projetId)),
        charger(API.planning(projetId)),
        charger(API.journal(projetId)),
        charger(API.membres(projetId)),
    ]);
    if (!projet) throw new Error("Projet introuvable");

    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    const M = 14;
    let y = 0;

    const titreSection = (texte) => {
        if (y > 255) { doc.addPage(); y = 20; }
        doc.setFillColor(...BLEU);
        doc.rect(M, y, W - 2 * M, 7, "F");
        doc.setTextColor(255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(texte, M + 2, y + 5);
        doc.setTextColor(0);
        y += 10;
    };
    const tableau = (head, body, opts = {}) => {
        autoTable(doc, {
            startY: y, head: [head], body,
            margin: { left: M, right: M },
            styles: { fontSize: 8.5, cellPadding: 1.8 },
            headStyles: { fillColor: [235, 240, 246], textColor: 30, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [248, 249, 251] },
            ...opts,
        });
        y = doc.lastAutoTable.finalY + 6;
    };
    const vide = (texte) => {
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...GRIS);
        doc.text(texte, M, y + 2); doc.setTextColor(0);
        y += 8;
    };

    // 2. En-tête
    try { doc.addImage(LOGO, "PNG", M - 1, 4, 40, 30); } catch { /* logo absent : on continue */ }
    doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("Rapport de projet", W - M, 14, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...GRIS);
    doc.text(`Édité le ${new Date().toLocaleDateString("fr-FR")} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`,
        W - M, 20, { align: "right" });
    doc.setTextColor(0);
    doc.setDrawColor(200); doc.line(M, 36, W - M, 36);

    doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    doc.text(`${projet.code ? projet.code + " — " : ""}${projet.nom}`, M, 45);
    const statut = findStatut(projet.statut), type = findType(projet.type);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    doc.text(`${type.label}  •  ${statut.label}  •  Priorité ${findPriorite(projet.priorite).label.toLowerCase()}`
        + (projet.enRetard ? "  •  EN RETARD" : ""), M, 51);
    y = 57;

    // 3. Indicateurs clés
    const kpis = [
        ["Avancement", pct(planning?.avancement ?? projet.avancement)],
        ["Budget", dh(projet.budget)],
        ["Produits nets", dh(finances?.produitsNets)],
        ["Charges", dh(finances?.chargesTotales)],
        ["Marge", finances ? `${dh(finances.marge)}${finances.tauxMarge != null ? ` (${finances.tauxMarge} %)` : ""}` : "—"],
    ];
    const kw = (W - 2 * M - 4 * 3) / 5;
    kpis.forEach(([lib, val], i) => {
        const x = M + i * (kw + 3);
        doc.setDrawColor(220); doc.setFillColor(248, 249, 251);
        doc.roundedRect(x, y, kw, 16, 1.5, 1.5, "FD");
        doc.setFontSize(7.5); doc.setTextColor(...GRIS); doc.text(lib.toUpperCase(), x + 2, y + 5);
        doc.setFontSize(9.5); doc.setTextColor(0); doc.setFont("helvetica", "bold");
        doc.text(doc.splitTextToSize(val, kw - 4), x + 2, y + 11);
        doc.setFont("helvetica", "normal");
    });
    y += 22;

    // 4. Informations générales
    titreSection("Informations générales");
    const infos = [
        ["Client", projet.clientName || "—", "Responsable", projet.responsableNom || "—"],
        ["Début prévu", dt(projet.dateDebut), "Fin prévue", dt(projet.dateFin)],
        ["Début réel", dt(projet.dateDebutReelle), "Fin réelle", dt(projet.dateFinReelle)],
        ["Lieu", [projet.lieu, projet.adresse, projet.ville].filter(Boolean).join(", ") || "—", "Budget", dh(projet.budget)],
    ];
    if (findType(projet.type).name === "Vente")
        infos.push(["Montant estimé", dh(projet.montantEstime), "Probabilité / étape",
            `${pct(projet.probabilite)} / ${findEtape(projet.etapeVente)?.label ?? "—"}`]);
    autoTable(doc, {
        startY: y, body: infos, theme: "plain", margin: { left: M, right: M },
        styles: { fontSize: 9, cellPadding: 1.4 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 32 }, 2: { fontStyle: "bold", cellWidth: 32 } },
    });
    y = doc.lastAutoTable.finalY + 3;
    if (projet.description) {
        doc.setFontSize(9);
        const lignes = doc.splitTextToSize(projet.description, W - 2 * M);
        doc.text(lignes, M, y + 3);
        y += lignes.length * 4.2 + 5;
    } else y += 3;

    // 5. Finances
    titreSection("Synthèse financière (HT)");
    if (finances) {
        tableau(["Rubrique", "Montant"], [
            ["Chiffre d'affaires (factures de vente)", dh(finances.chiffreAffaires)],
            ["− Retours clients", dh(finances.retoursClients)],
            [{ content: "= Produits nets", styles: { fontStyle: "bold" } }, { content: dh(finances.produitsNets), styles: { fontStyle: "bold" } }],
            ["Factures d'achat", dh(finances.achatsFactures)],
            ["− Retours fournisseurs", dh(finances.retoursFournisseurs)],
            ["+ Dépenses directes", dh(finances.depensesDirectes)],
            ["+ Main-d'œuvre (tâches)", dh(finances.mainOeuvreTaches)],
            [{ content: "= Charges totales", styles: { fontStyle: "bold" } }, { content: dh(finances.chargesTotales), styles: { fontStyle: "bold" } }],
            [{ content: "Marge", styles: { fontStyle: "bold", textColor: finances.marge < 0 ? ORANGE : [0, 0, 0] } },
             { content: `${dh(finances.marge)}${finances.tauxMarge != null ? `  (${finances.tauxMarge} %)` : ""}`, styles: { fontStyle: "bold", textColor: finances.marge < 0 ? ORANGE : [0, 0, 0] } }],
            ["Engagé (commandes fournisseurs non reçues)", dh(finances.engage)],
            ["Reste sur budget", dh(finances.resteBudget)],
            ["Consommation du budget", pct(finances.consommationBudget)],
        ], { columnStyles: { 1: { halign: "right", cellWidth: 55 } } });

        if (finances.parCategorie?.length) {
            tableau(["Charges par catégorie", "Montant"],
                finances.parCategorie.map((c) => [findCategorieDepense(c.categorie)?.label ?? c.categorie, dh(c.montant)]),
                { columnStyles: { 1: { halign: "right", cellWidth: 55 } } });
        }
    } else vide("Finances indisponibles.");

    // 6. Documents commerciaux
    const docs = finances?.documents ?? [];
    titreSection(`Documents commerciaux (${docs.length})`);
    if (docs.length) {
        tableau(["Date", "Type", "Numéro", "Tiers", "HT", "Rôle"],
            docs.map((d) => [dt(d.dateDocument), DOC_COMMERCIAL_LABELS[d.documentType] ?? d.documentType,
                d.numero, d.tiers ?? "—", dh(d.totalHT), d.compte ? (d.role ?? "") : "Non compté"]),
            { columnStyles: { 4: { halign: "right" } } });
    } else vide("Aucun document rattaché.");

    // 7. Planning
    titreSection("Planning");
    if (planning) {
        doc.setFontSize(9);
        doc.text(`${planning.nbTerminees}/${planning.nbTaches} tâches terminées  •  ${planning.nbEnRetard} en retard  •  `
            + `${h(planning.heuresReelles)} réalisées sur ${h(planning.heuresEstimees)} estimées  •  coût main-d'œuvre ${dh(planning.coutMainOeuvre)}`,
            M, y + 2);
        y += 7;
        const lignes = [];
        const ajouterTaches = (taches) => taches.forEach((t) => lignes.push([
            `   ${t.titre}`, t.assigneNom ?? "—", dt(t.echeance), findStatutTache(t.statut).label,
            pct(t.avancement), t.enRetard ? "Oui" : "",
        ]));
        (planning.phases ?? []).forEach((p) => {
            lignes.push([{ content: `${p.nom}  (${p.nbTerminees}/${p.nbTaches} — ${p.avancement} %)`, colSpan: 6,
                styles: { fontStyle: "bold", fillColor: [235, 240, 246] } }]);
            ajouterTaches(p.taches ?? []);
        });
        if (planning.tachesSansPhase?.length) {
            lignes.push([{ content: "Sans phase", colSpan: 6, styles: { fontStyle: "bold", fillColor: [235, 240, 246] } }]);
            ajouterTaches(planning.tachesSansPhase);
        }
        if (lignes.length) tableau(["Tâche", "Assigné", "Échéance", "Statut", "Avanc.", "Retard"], lignes);
        else vide("Aucune tâche planifiée.");
    } else vide("Planning indisponible.");

    // 8. Équipe
    const equipe = Array.isArray(membres) ? membres : [];
    titreSection(`Équipe (${equipe.length})`);
    if (equipe.length) {
        tableau(["Nom", "Poste", "Rôle sur le projet", "Téléphone"],
            equipe.map((m) => [m.nomComplet, m.poste ?? "—", m.role ?? "—", m.telephone ?? "—"]));
    } else vide("Aucun membre affecté.");

    // 9. Journal (10 dernières entrées + incidents ouverts)
    const entrees = journal?.entrees ?? [];
    titreSection(`Journal de bord — ${journal?.incidentsOuverts ?? 0} incident(s) ouvert(s)`);
    if (entrees.length) {
        const dernieres = [...entrees].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);
        tableau(["Date", "Type", "Titre", "Gravité", "État"],
            dernieres.map((e) => [dt(e.date), findTypeJournal(e.type).label, e.titre,
                findGravite(e.gravite)?.label ?? "—",
                findTypeJournal(e.type).name === "Incident" ? (e.resolu ? "Résolu" : "Ouvert") : ""]),
            { columnStyles: { 2: { cellWidth: 80 } } });
        if (entrees.length > 10) vide(`… ${entrees.length - 10} entrée(s) plus ancienne(s) non affichée(s).`);
    } else vide("Aucune entrée au journal.");

    // 10. Pied de page sur toutes les pages
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(...GRIS);
        doc.text(`${projet.code ?? ""} ${projet.nom}`.trim(), M, 290);
        doc.text(`Page ${i} / ${n}`, W - M, 290, { align: "right" });
    }

    // 11. Sortie
    const nom = `Rapport_${(projet.code || projet.nom).replace(/[^\w-]+/g, "_")}.pdf`;
    if (imprimer) {
        const url = URL.createObjectURL(doc.output("blob"));
        const win = window.open(url);
        if (win) win.onload = () => { win.focus(); win.print(); };
    } else {
        doc.save(nom);
    }
}
